import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { Skeleton, Table } from 'antd';
import { ExpandableConfig } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';

import {
  CountryWithCalendarsDTO,
  PAGE_SIZE,
  calendarStore,
  calendarsService,
  DEFAULT_SORT_ORDER,
  DEFAULT_ACTIVE,
} from 'entities/calendar';

import { useDebounceFunction } from 'shared/lib/debounce';
import { useElementSizes, withSuspense } from 'shared/lib/react';

import { Loader } from 'shared/ui/Loader';
import { EmptyText } from '../EmtyText';
import { ExpandedCalendarsList } from '../ExpandedCalendarsList';
import { ExpandIcon } from '../ExpandIcon';

import { BORDER_WIDTH, getColumns, HEADER_HEIGHT, HEIGHT_THRESHOLD, SEARCH_QUERY_LENGTH, TOTAL_WIDTH } from './const';

import styles from './Table.module.scss';

const CalendarsTable: React.FC = () => {
  const { selectedYear, searchQuery, setSearchQuery } = useStore(calendarStore);
  const [expandedList, setExpandedList] = useState<string[]>([]);

  const { t } = useTranslation('Calendar');

  const { data, isFetchingNextPage, hasNextPage, fetchNextPage, isLoading } = useSuspenseInfiniteQuery(
    calendarsService.queryOptions({
      active: DEFAULT_ACTIVE,
      size: PAGE_SIZE,
      year: selectedYear.toString(),
      searchName: searchQuery?.trim().length >= SEARCH_QUERY_LENGTH ? searchQuery.trim() : '',
      sort: DEFAULT_SORT_ORDER,
    }),
  );

  const parentRef = React.useRef<HTMLDivElement>(null);
  const { height: tableHeight, width: tableWidth } = useElementSizes(parentRef, {
    excludeWidth: BORDER_WIDTH,
    excludeHeight: HEADER_HEIGHT,
  });

  const onExpand = useCallback((expanded: boolean, record: CountryWithCalendarsDTO['id']): void => {
    if (!expanded) {
      setExpandedList((prev) => prev.filter((item) => item !== record));
    } else {
      setExpandedList((prev) => [...prev.filter((item) => item !== record), record]);
    }
  }, []);

  const mappedData =
    data?.pages?.reduce<CountryWithCalendarsDTO[]>((acc, { content }) => [...acc, ...content], []) || [];

  useEffect(() => {
    if (searchQuery?.trim().length >= SEARCH_QUERY_LENGTH) {
      setExpandedList((prev) => {
        const ids = mappedData.map((item) => item.id);
        return prev.length === ids.length && prev.every((id) => ids.includes(id)) ? prev : ids;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappedData]);

  const handleScroll: React.UIEventHandler<HTMLDivElement> = useCallback(() => {
    const element = parentRef.current?.querySelector('.ant-table-tbody-virtual-holder-inner');
    const parentEl = element?.parentElement;

    const { height = 0, top = 0 } = parentEl?.getBoundingClientRect() || {};
    const { bottom = 0 } = element?.getBoundingClientRect() || {};

    const shouldFetchNextPage = height + top - bottom < HEIGHT_THRESHOLD;
    if (shouldFetchNextPage && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const debouncedHandleScroll = useDebounceFunction(handleScroll, 100);

  if (isFetchingNextPage) {
    mappedData.push({
      id: 'loading',
      country: {
        flag: null,
        englishName: <Skeleton active paragraph={false} />,
      },
      calendarsCount: <Skeleton active paragraph={false} />,
      nonWorkingDays: <Skeleton active paragraph={false} />,
      workingDays: <Skeleton active paragraph={false} />,
      calendars: [],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  }

  const expandConfig: ExpandableConfig<CountryWithCalendarsDTO> = useMemo(() => {
    return {
      expandIcon: ExpandIcon,
      expandedRowRender: ExpandedCalendarsList,
      columnWidth: 32,
      onExpand: (expanded, record) => onExpand(expanded, record.id),
      expandedRowKeys: expandedList,
    };
  }, [onExpand, expandedList]);

  return (
    <div className={styles.container} ref={parentRef}>
      <Table
        columns={getColumns(t)}
        locale={{
          emptyText: isLoading ? null : <EmptyText tableHeight={tableHeight} setSearchQuery={setSearchQuery} />,
        }}
        loading={isLoading}
        pagination={false}
        rowKey="id"
        onScroll={debouncedHandleScroll}
        bordered
        virtual
        sticky
        style={{ height: '100%' }}
        className={styles.wrapperTable}
        dataSource={mappedData}
        scroll={{
          y: tableHeight,
          x: tableWidth > TOTAL_WIDTH ? tableWidth : TOTAL_WIDTH,
        }}
        expandable={expandConfig}
      />
    </div>
  );
};

export default withSuspense(CalendarsTable, { fallback: <Loader /> });
